import {useCallback, useEffect, useState} from "react";
import {Navigate} from "react-router-dom";
import ReactGA from "react-ga4";
import {DynamicTemplateLoader} from "@/components/ui/DynamicTemplateLoader.tsx";
import {QuizData} from "@/types/quiz-types.ts";
import {loadTemplateConfig} from "@/utils/get-template-config.ts";
import {useTemplate} from "@/contexts/templateContext.tsx";
import {Progress} from "@/components/ui/Progress.tsx";
import {BIT_SHIFT, GroupsType} from "@/types/step.ts";
import {getTemplateResults} from "@/utils/get-template-results.ts";
import {getTemplateGroups} from "@/utils/get-template-groups.ts";
import {useCookies} from "react-cookie";
import {htmlToString} from "@/utils/html-to-string.ts";

export const Quiz = () => {
    const [step, setStep] = useState<number>(1);
    const [points, setPoints] = useState<Record<string, number>>({});
    const [animate, setAnimate] = useState<boolean>(false);
    const [quizData, setQuizData] = useState<QuizData>();
    const [groupsCounter, setGroupsCounter] = useState<Record<string, number>>({});
    const [groups, setGroups] = useState<GroupsType[]>();
    const [_, setCookie] = useCookies([import.meta.env.VITE_COOKIE_NAME]);
    const { template } = useTemplate();

    const questions = quizData ? quizData.questions : null;
    const isFinished = questions && step > questions?.length;
    const currentAnswer = quizData ? (quizData.questions[step - 1] ?? null) : null;

    const progressValue = questions ? ((step / questions.length) * 100) : 0;

    const handlePickA = useCallback(() => {
        setAnimate(true);
        setTimeout(() => {
            setAnimate(false);
            if (currentAnswer?.options[0]) {
                setPoints((prevPoints => {
                    const points = currentAnswer.options[0].points ?? 1;
                    const group = currentAnswer.options[0].group;

                    const newPoints = { ...prevPoints };
                    if (!newPoints[group]) {
                        newPoints[group] = 0;
                    }
                    newPoints[group] += points;

                    return newPoints;
                }));
                setStep((prevStep) => prevStep + 1);
            }
        }, 500);

        ReactGA.event({
            category: "quiz",
            action: "answer",
            label: htmlToString(currentAnswer?.options[0].label),
            value: currentAnswer?.options[0].points,
        });
    }, [currentAnswer]);

    const handlePickB = useCallback(() => {
        setAnimate(true);
        setTimeout(() => {
            setAnimate(false);
            if (currentAnswer?.options[1]) {
                setPoints((prevPoints => {
                    const points = currentAnswer.options[1].points ?? 1;
                    const group = currentAnswer.options[1].group;

                    const newPoints = { ...prevPoints };
                    if (!newPoints[group]) {
                        newPoints[group] = 0;
                    }
                    newPoints[group] += points;

                    return newPoints;
                }));
                setStep((prevStep) => prevStep + 1);
            }
        }, 500);

        ReactGA.event({
            category: "quiz",
            action: "answer",
            label: htmlToString(currentAnswer?.options[1].label),
            value: currentAnswer?.options[1].points,
        });
    }, [currentAnswer]);

    useEffect(() => {
        if (!isFinished) {
            ReactGA.send({
                hitType: 'pageview',
                page: `/quiz/${step}`,
            });
        }
    }, [step])

    useEffect(() => {
        loadTemplateConfig(template).then((data)=>{
            setQuizData(data);
            data.questions.forEach((question, qIndex) => {
                question.options.forEach((option, oIndex) => {
                    if (option.backgroundImage) {
                        const img = new Image();
                        img.src = option.backgroundImage;

                        img.onload = () => {
                            console.log(`✅ Preloaded [Q${qIndex + 1}, Option ${oIndex + 1}]`);
                        };

                        img.onerror = () => {
                            console.warn(`⚠️ Failed to preload [Q${qIndex + 1}, Option ${oIndex + 1}]`);
                        };
                    }
                });
            });
        });
        getTemplateGroups(template).then((groups) => {
            setGroups(groups as GroupsType[]);
        });
    }, [template]);

    useEffect(() => {
        setCookie("points", JSON.stringify(points), { expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 100)});
    }, [points]);

    if (!quizData) return <div>Ładowanie danych</div>;

    return (
        <>
            {/*<div className="fixed top-0 left-0 w-full h-[10px]">*/}
            {/*    <Progress className="w-full h-full" value={progressValue}/>*/}
            {/*</div>*/}
            {currentAnswer && (
                <DynamicTemplateLoader screenType={"QuizScreen"} componentProps={{
                    onPickAnswerA: handlePickA,
                    onPickAnswerB: handlePickB,
                    quizQuestion: quizData.questions[step - 1],
                    progressValue: progressValue,
                    animate,
                }} />
            )}
            {isFinished && <Navigate to={`/result/${points}`}/>}
        </>
    );
};
